import type { ISurveyResponseModel } from "@lib/types";

type MockQuestion = NonNullable<ISurveyResponseModel["questions"]>[number];

export const completeSurveyResponse: ISurveyResponseModel = {
  pageId: 0,
  localeKnown: true,
  completionPercentage: 100,
  completionStatus: "Complete",
  questions: [],
};

/**
 * Catalog of mock questions. Grouped into {@link surveyPages} — one question per
 * page today so demos match real API pacing. Put multiple questions in a page's
 * `questions` array to exercise multi-question forms later.
 */
const mockQuestions: MockQuestion[] = [
  {
    id: 1,
    text: "What is your primary role?",
    required: true,
    helpText: "Choose the option that best describes you.",
    kind: {
      single: {
        isSearchable: true,
        choices: [
          { id: 1, text: "Developer", exclusive: false, selected: false, isSpecifyAnswerType: false },
          { id: 2, text: "Designer", exclusive: false, selected: false, isSpecifyAnswerType: false },
          { id: 3, text: "Product Manager", exclusive: false, selected: false, isSpecifyAnswerType: false },
          { id: 4, text: "Other", exclusive: false, selected: false, isSpecifyAnswerType: true },
        ],
      },
    },
  },
  {
    id: 2,
    text: "How many years of experience do you have?",
    kind: {
      single: {
        isSearchable: false,
        choices: [
          { id: 5, text: "Less than 1 year", exclusive: false, selected: false, isSpecifyAnswerType: false },
          { id: 6, text: "1–3 years", exclusive: false, selected: false, isSpecifyAnswerType: false },
          { id: 7, text: "4–7 years", exclusive: false, selected: false, isSpecifyAnswerType: false },
          { id: 8, text: "8 or more years", exclusive: false, selected: false, isSpecifyAnswerType: false },
        ],
      },
    },
  },
  {
    id: 3,
    text: "What IDEs do you use?",
    required: true,
    helpText: "Select all that apply.",
    kind: {
      multiple: {
        minAnswers: 1,
        isSearchable: true,
        choices: [
          { id: 13, text: "Visual Studio Code" },
          { id: 14, text: "Visual Studio" },
          { id: 15, text: "JetBrains (IntelliJ, Rider, etc.)" },
          { id: 16, text: "Cursor" },
          { id: 17, text: "Vim / Neovim" },
          { id: 18, text: "Other", isSpecifyAnswerType: true },
          { id: 19, text: "None of the above", exclusive: true },
        ],
      },
    },
  },
  {
    id: 4,
    text: "What company do you work for?",
    required: true,
    helpText: "Enter your employer name.",
    kind: {
      text: {
        placeholder: "e.g. Acme Corp",
        format: "[A-Za-z0-9 .,&'-]+",
      },
    },
  },
  {
    id: 5,
    text: "Please provide your name",
    required: true,
    helpText: "Enter your first and last name.",
    kind: {
      text: {
        inputs: {
          "101": "First name",
          "102": "Last name",
        },
      },
    },
  },
  {
    id: 6,
    text: "Approximately how many hours per week do you spend on each activity?",
    required: true,
    helpText: "Enter your best estimate for each.",
    kind: {
      numeric: {
        inputs: {
          "201": "Writing code",
          "202": "Code review",
          "203": "Meetings & planning",
        },
      },
    },
  },
  {
    id: 7,
    text: "How many people are on your immediate engineering team?",
    helpText: "Include engineers, QA, and tech leads you work with directly.",
    kind: {
      numeric: {
        placeholder: "e.g. 12",
      },
    },
  },
  {
    id: 8,
    text: "Software delivery practices",
    required: true,
    helpText:
      "Estimate based on your team's typical workflow over the past month, not one-off exceptions.",
    kind: {
      singleSelectGrid: [
        {
          id: 801,
          questionText: "How often do you deploy to production?",
          choices: [
            { id: 8011, text: "Daily" },
            { id: 8012, text: "Weekly" },
            { id: 8013, text: "Monthly" },
          ],
        },
        {
          id: 802,
          questionText: "How often do you run automated tests in CI?",
          choices: [
            { id: 8021, text: "Daily" },
            { id: 8022, text: "Weekly" },
            { id: 8023, text: "Monthly" },
          ],
        },
      ],
    },
  },
  {
    id: 9,
    text: "Additional process questions",
    required: true,
    helpText: "These items use different response scales per question.",
    kind: {
      singleSelectGrid: [
        {
          id: 901,
          questionText: "How satisfied are you with your release process?",
          choices: [
            { id: 9011, text: "Very dissatisfied" },
            { id: 9012, text: "Neutral" },
            { id: 9013, text: "Very satisfied" },
          ],
        },
        {
          id: 902,
          questionText: "Do you have a formal on-call rotation?",
          choices: [
            { id: 9021, text: "Yes" },
            { id: 9022, text: "No" },
          ],
        },
        {
          id: 903,
          questionText:
            "How satisfied are you with your incident response process?",
          choices: [
            { id: 9031, text: "Not at all satisfied" },
            { id: 9032, text: "Somewhat satisfied" },
            { id: 9033, text: "Extremely satisfied" },
          ],
        },
      ],
    },
  },
  {
    id: 10,
    text: "Describe a recent technical challenge you solved",
    required: true,
    helpText: "Share enough context for a peer to understand the problem and approach.",
    kind: {
      text: {
        isMultiline: true,
        placeholder: "Describe the challenge and how you approached it…",
        defaultValue: "",
      },
    },
  },
  {
    id: 11,
    text: "Tell us more about your work environment",
    required: true,
    helpText:
      "CMIX Text-Area (realAnswer) — prompts come from kind.realAnswer.inputs.",
    kind: {
      realAnswer: {
        isMultiline: true,
        placeholder: "Additional details…",
        inputs: {
          "45081": "Describe your typical workspace",
        },
      },
    },
  },
];

/**
 * Build ordered API pages from question groups. Each group is one response
 * payload; length of a group is how many questions the "API" returns at once.
 */
export function buildSurveyPages(
  questionGroups: MockQuestion[][],
): ISurveyResponseModel[] {
  const total = questionGroups.length;
  return questionGroups.map((questions, index) => ({
    pageId: index + 1,
    localeKnown: true,
    completionPercentage:
      total <= 1 ? 0 : Math.round((index / total) * 100),
    completionStatus: "InProcess" as const,
    questions,
  }));
}

/** Default mock: one question per page (matches typical profiling API pacing). */
export const surveyPages: ISurveyResponseModel[] = buildSurveyPages(
  mockQuestions.map((question) => [question]),
);

/** @deprecated Use {@link surveyPages}; kept for callers that expected a single payload. */
export const singleKindSurveys: ISurveyResponseModel = surveyPages[0]!;
